import { manifestApi } from "#/api/manifestApi";
import ExitStatusBadge from "#/components/manifests/ExitStatusBadge";
import ReleaseBadge from "#/components/manifests/ReleaseBadge";
import { showSuccessNotification } from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import { EmptyState } from "#/components/ui/EmptyState";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { HawbDetail, HawbFormPayload, HawbUpdatePreview } from "#/types/manifest";
import { formatHawbLabel } from "#/utils/manifestUtils";
import { logger } from "#/utils/logger";
import { Accordion, Button, Modal, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import HawbChangeSummary from "../shipmentmanifests/components/HawbChangeSummary";
import HawbDetailsFields from "../shipmentmanifests/components/HawbDetailsFields";
import { hawbDetailsValidation, toHawbPayload, type HawbDetailsValues } from "../shipmentmanifests/components/hawbForm";
import DeleteHawbModal from "./components/DeleteHawbModal";
import ExitIssueBox from "./components/ExitIssueBox";
import ExitItemsSection from "./components/ExitItemsSection";
import HawbItemsTable from "./components/HawbItemsTable";

const LIST_PATH = "/fleet/shipment-manifests";

const toFormValues = (hawb: HawbDetail): HawbDetailsValues => ({
  arrival_date: hawb.arrival_date,
  origin_station: hawb.origin_station ?? "",
  destination: hawb.destination ?? "",
  responsible_person: hawb.responsible_person ?? "",
  packages: hawb.packages,
  weight_kg: hawb.weight_kg ?? "",
  storage_fee_paid: hawb.storage_fee_paid,
  blocked: hawb.blocked,
  has_pick_list: hawb.has_pick_list,
});

const ShipmentManifestDetailPage = () => {
  const { id } = useParams();
  const hawbId = Number(id);
  const validId = Number.isInteger(hawbId) && hawbId > 0;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canWrite, canDelete } = usePermission();
  const [isEditMode, setIsEditMode] = useState(false);
  const [pending, setPending] = useState<{ payload: HawbFormPayload; preview: HawbUpdatePreview } | null>(
    null,
  );
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);

  const {
    data: hawb,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["shipment-manifest", hawbId],
    queryFn: async () => (await manifestApi.getById(hawbId)).data,
    enabled: validId,
    retry: false,
  });

  const form = useForm<HawbDetailsValues>({
    initialValues: {
      arrival_date: null,
      origin_station: "",
      destination: "",
      responsible_person: "",
      packages: "",
      weight_kg: "",
      storage_fee_paid: false,
      blocked: false,
      has_pick_list: false,
    },
    validate: hawbDetailsValidation,
  });

  // Reload the form whenever fresh data arrives and the user is not editing.
  useEffect(() => {
    if (hawb && !isEditMode) {
      form.setValues(toFormValues(hawb));
      form.resetDirty(toFormValues(hawb));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hawb, isEditMode]);

  const previewMutation = useMutation({
    mutationFn: (payload: HawbFormPayload) => manifestApi.updatePreview(hawbId, payload),
    onSuccess: (response, payload) => {
      const preview = response.data;
      if (
        preview.changes.length === 0 &&
        preview.items_to_create === 0 &&
        preview.items_to_remove.length === 0
      ) {
        setIsEditMode(false);
        return;
      }
      setPending({ payload, preview });
    },
    onError: (error) => logger.error("consignment update preview error:", error),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: HawbFormPayload) => manifestApi.update(hawbId, payload),
    onSuccess: () => {
      showSuccessNotification({ message: t("manifests.detail.updated") });
      setPending(null);
      setIsEditMode(false);
      void refetch();
    },
    onError: (error) => logger.error("consignment update error:", error),
  });

  const cancelEdit = () => {
    if (hawb) form.setValues(toFormValues(hawb));
    form.clearErrors();
    setIsEditMode(false);
  };

  if (!validId || isError) {
    return (
      <DetailPageLayout title={t("manifests.detail.title")} backTo={LIST_PATH}>
        <EmptyState title={t("manifests.detail.notFound")} />
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout
      title={
        hawb ? (
          <span className="flex flex-wrap items-center gap-2">
            {formatHawbLabel(hawb)}
            <ReleaseBadge releaseBlockers={hawb.release_blockers} />
            <ExitStatusBadge status={hawb.exit_status} issues={hawb.exit_issues} />
          </span>
        ) : (
          t("manifests.detail.title")
        )
      }
      loading={isPending}
      backTo={LIST_PATH}
      navRightSection={
        hawb && (
          <div className="flex items-center gap-2">
            {isEditMode ? (
              <>
                <Button size="xs" color="black" variant="outline" onClick={cancelEdit}>
                  {t("common.cancel")}
                </Button>
                <Button
                  size="xs"
                  color="black"
                  loading={previewMutation.isPending}
                  onClick={() => {
                    if (form.validate().hasErrors) return;
                    previewMutation.mutate(toHawbPayload(form.values));
                  }}
                >
                  {t("common.save")}
                </Button>
              </>
            ) : (
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  size="xs"
                  color="black"
                  leftSection={<IconEdit size={16} />}
                  disabled={!canWrite()}
                  onClick={() => setIsEditMode(true)}
                >
                  {t("common.edit")}
                </Button>
              </PermissionTooltip>
            )}
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                size="xs"
                color="red"
                variant="outline"
                leftSection={<IconTrash size={16} />}
                disabled={!canDelete() || isEditMode}
                onClick={openDelete}
              >
                {t("common.delete")}
              </Button>
            </PermissionTooltip>
          </div>
        )
      }
    >
      {hawb && (
        <div className="w-full flex flex-col pb-6">
          <ExitIssueBox hawb={hawb} />
          <Accordion variant="separated" multiple defaultValue={["info", "exit", "items"]}>
            <Accordion.Item value="info">
              <Accordion.Control>{t("manifests.detail.sections.info")}</Accordion.Control>
              <Accordion.Panel>
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput label={t("manifests.columns.awb")} value={hawb.awb_number} readOnly disabled />
                    <TextInput
                      label={t("manifests.columns.hawb")}
                      value={hawb.hawb_number || "—"}
                      readOnly
                      disabled
                    />
                  </div>
                  <HawbDetailsFields form={form} disabled={!isEditMode} />
                  <span className="text-sm text-gray-600">
                    {t("manifests.detail.taggedSummary", {
                      tagged: hawb.tagged_count,
                      items: hawb.items_count,
                      packages: hawb.packages,
                    })}
                  </span>
                </div>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="exit">
              <Accordion.Control>
                {hawb.exit_status === "issue"
                  ? t("manifests.detail.sections.itemsConcerned")
                  : t("manifests.detail.sections.itemsScanned")}
              </Accordion.Control>
              <Accordion.Panel>
                <ExitItemsSection hawb={hawb} />
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="items">
              <Accordion.Control>
                {t("manifests.detail.sections.items", { count: hawb.items.length })}
              </Accordion.Control>
              <Accordion.Panel>
                <HawbItemsTable items={hawb.items} />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Modal
            opened={pending !== null}
            onClose={() => setPending(null)}
            centered
            size="lg"
            title={t("manifests.detail.confirmTitle")}
            closeOnClickOutside={false}
            classNames={{ title: "font-bold" }}
          >
            {pending && (
              <div className="flex flex-col gap-4">
                <span className="text-sm">
                  {t("manifests.detail.confirmIntro", { label: formatHawbLabel(hawb) })}
                </span>
                <HawbChangeSummary
                  changes={pending.preview.changes}
                  itemsToCreate={pending.preview.items_to_create}
                  itemsToRemove={pending.preview.items_to_remove}
                />
                <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
                  <Button size="sm" color="black" variant="outline" onClick={() => setPending(null)}>
                    {t("common.cancel")}
                  </Button>
                  <Button
                    size="sm"
                    color="black"
                    loading={updateMutation.isPending}
                    onClick={() => updateMutation.mutate(pending.payload)}
                  >
                    {t("common.confirm")}
                  </Button>
                </div>
              </div>
            )}
          </Modal>

          <DeleteHawbModal
            opened={deleteOpened}
            onClose={closeDelete}
            hawb={hawb}
            onDeleted={() => void navigate(LIST_PATH)}
          />
        </div>
      )}
    </DetailPageLayout>
  );
};

export default ShipmentManifestDetailPage;
